import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import DataTable from '../../ui/DataTable/DataTable';
import Button from '../../ui/Button/Button';
import Badge from '../../ui/Badge';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import { Input, Select, FormFooter } from '../../ui/Field/Field';
import { toast, confirmDialog } from '../../ui/feedback';
import LoadingSpinner from '../../components/LoadingSpinner';
import { finesService, errorMessage } from '../../services/finesService';
import { usePermissions } from '../../hooks/usePermissions';
import AutoEjarAccountCard from './AutoEjarAccountCard';

// Administration > Schedules: when each engine task runs, editable live.
// Four grids: the day by hour (24 columns, a daily task sits in its hour's cell), the
// repeating tasks, all schedules (one band per area, one editable cell per row) and the
// change history. The engine validates, records who changed what, applies the value at
// once (loops re-read within 5 minutes) and never starts or stops a task from here.

// a cell whose text wraps (the table's own rule keeps every cell on one line)
const WRAP = { whiteSpace: 'normal' };
const UNIT = { minutes: 'min', seconds: 'sec', hours: 'h', days: 'days', count: 'times' };
// Each area keeps its own colour (design-system tokens) so a task is recognisable across the grids.
const GROUPS = [
    { id: 'rta', title: 'RTA fines', color: 'var(--accent)', soft: 'var(--accent-soft)', keys: ['portal_daily_time', 'portal_quick_interval_min', 'auto_run_interval_s'] },
    { id: 'ejar', title: 'Ejar', color: 'var(--info)', soft: 'var(--info-soft)', keys: ['ejar_interval_min', 'ejar_check_time', 'ejar_contacts_days'] },
    { id: 'salik', title: 'Salik & Darb', color: 'var(--warning)', soft: 'var(--warning-soft)', keys: ['salik_daily_time', 'salik_interval_min'] },
    // automatic Ejar registration (owner, 2 Oct 2026): off until an admin turns it on and sets the day it starts from
    { id: 'auto', title: 'Ejar auto-registration', color: 'var(--danger)', soft: 'var(--danger-soft)', keys: ['auto_ejar_parking', 'auto_ejar_fines', 'auto_ejar_salik', 'auto_ejar_from', 'auto_ejar_parking_min', 'auto_ejar_salik_time', 'auto_ejar_salik_day', 'auto_ejar_invygo_month_end', 'auto_ejar_invygo_month_end_time', 'auto_ejar_retries', 'auto_ejar_retry_hours'] },
    { id: 'reports', title: 'Reports & alerts', color: 'var(--success)', soft: 'var(--success-soft)', keys: ['telegram_report_time', 'alert_stale_hours'] },
    // Operations > Brokers (owner, 23 Oct 2026): each booking site's read - Rentway and Comfy
    { id: 'brokers', title: 'Brokers (booking sites)', color: 'var(--info)', soft: 'var(--info-soft)', keys: ['rentway_interval_h', 'comfy_interval_h'] },
    { id: 'house', title: 'Housekeeping', color: 'var(--text-3)', soft: 'var(--surface-2)', keys: ['server_restart_time', 'backup_keep_days', 'output_keep_days', 'backup_bundle', 'refunds_list', 'rent_changes'] },
];
const SHORT = { portal_daily_time: 'RTA run', ejar_check_time: 'Ejar check', salik_daily_time: 'Salik', telegram_report_time: 'Report', backup_bundle: 'Backup', refunds_list: 'Refunds', rent_changes: 'Rent', auto_ejar_salik_time: 'Salik line', auto_ejar_invygo_month_end_time: 'Invygo month end', server_restart_time: 'Restart' };
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];   // the engine's 0 = Monday
const NEVER_DAY = 7;   // the monthly Salik lines written by hand (owner, 8 Oct 2026) - the engine's SALIK_BY_HAND
const SOURCE = {
    override: ['accent', 'changed here'],
    env: ['warning', 'server .env'],
    default: ['neutral', 'default'],
    fixed: ['neutral', 'fixed'],
};
const groupOf = (key) => GROUPS.find((g) => g.keys.includes(key)) || GROUPS[GROUPS.length - 1];
const minutesOf = (t) => { const m = /^(\d{2}):(\d{2})$/.exec(t || ''); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
const every = (v, kind) => {
    if (v === 0) return 'off';
    if (kind === 'minutes') return v % 60 === 0 ? `every ${v / 60} h` : `every ${v} min`;
    if (kind === 'seconds') return v % 3600 === 0 ? `every ${v / 3600} h` : `every ${v} sec`;
    return `${v} ${UNIT[kind] || ''}`;
};
const dubaiNow = () => {
    try { return new Date().toLocaleTimeString('en-GB', { timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit' }); } catch { return null; }
};

// Last run + whether it worked: the engine also sends lastError / lastOk, so a failed run no
// longer reads as done (26 Sep 2026 audit).
const LastRun = ({ row }) => (
    <>
        {row.lastRun || '—'}
        {row.lastError && (
            <span className="block text-[11px] font-sans font-semibold text-red-700" title={row.lastError}>
                failed{row.lastOk ? ` · last OK ${row.lastOk}` : ''}
            </span>
        )}
    </>
);

const GroupDot = ({ group }) => (
    <span aria-hidden="true" className="inline-block w-2.5 h-2.5 rounded-full me-2 align-middle" style={{ background: group.color }} />
);

const Section = ({ title, right, children }) => (
    <section className="mb-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
            <h3 className="text-base font-semibold text-gray-900 m-0">{title}</h3>
            {right && <div className="text-xs text-gray-600">{right}</div>}
        </div>
        {children}
    </section>
);

/** 24 columns, one per hour; every daily task sits in its hour's cell - nothing can overlap. */
const HourGrid = ({ rows, value, nowHour }) => {
    const marks = rows.filter((r) => r.kind === 'time' && minutesOf(value(r)) !== null)
        .map((r) => ({ key: r.key, label: SHORT[r.key] || r.label, time: value(r), min: minutesOf(value(r)), group: groupOf(r.key) }))
        .sort((a, b) => a.min - b.min);
    const hours = Array.from({ length: 24 }, (_, h) => h);
    return (
        <TableContainer>
            <Table>
                <TableHead><TableRow>{hours.map((h) => (
                    <TableHeader key={h} className={h === nowHour ? 'text-red-700' : ''} title={h === nowHour ? 'now' : undefined}>
                        <span className="inline-flex items-center gap-1">
                            {String(h).padStart(2, '0')}
                            {h === nowHour && <><span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-red-600" /><span className="sr-only">(now)</span></>}
                        </span>
                    </TableHeader>))}</TableRow></TableHead>
                <TableBody>
                    <TableRow>{hours.map((h) => {
                        const here = marks.filter((m) => Math.floor(m.min / 60) === h);
                        return (
                            <TableCell key={h} className={`align-top ${h === nowHour ? 'bg-red-50' : ''}`} style={{ height: 60 }}>
                                {here.map((m) => (
                                    <div key={m.key} className="block my-0.5 px-1 py-0.5 rounded text-[11px] leading-tight font-semibold"
                                         style={{ border: `1px solid ${m.group.color}`, color: m.group.color, background: m.group.soft }}>
                                        {m.label}<br /><span className="font-mono">{m.time}</span>
                                    </div>
                                ))}
                            </TableCell>
                        );
                    })}</TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default function EngineSchedule() {
    const { hasPermission } = usePermissions();
    const canReset = hasPermission('schedule_reset');
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({});
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setError(null);
        try { setData(await finesService.engineSchedule()); setDraft({}); } catch (e) { setError(errorMessage(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const rows = useMemo(() => data?.rows || [], [data]);
    const byKey = useMemo(() => Object.fromEntries(rows.map((r) => [r.key, r])), [rows]);
    const value = useCallback((r) => (draft[r.key] !== undefined ? draft[r.key] : (r.value ?? '')), [draft]);
    const changed = Object.entries(draft).filter(([k, v]) => String(v) !== String(byKey[k]?.value ?? ''));
    const set = (key, v) => setDraft((d) => ({ ...d, [key]: v }));
    const fmt = (v, kind) => {
        if (kind === 'switch') return v === true || v === 'true' ? 'on' : 'off';
        if (v === null || v === undefined || v === '') return '—';
        if (kind === 'time') return v;
        if (kind === 'date') return `from ${String(v).slice(8, 10)}/${String(v).slice(5, 7)}/${String(v).slice(0, 4)}`;
        if (kind === 'weekday') return Number(v) === NEVER_DAY ? 'never - by hand' : `every ${WEEKDAYS[Number(v)] || v}`;
        return every(Number(v), kind);
    };

    const save = async () => {
        if (!changed.length) return;
        const summary = changed.map(([k, v]) => `• ${byKey[k]?.label}: ${byKey[k]?.value} → ${v}`).join('\n');
        if (!(await confirmDialog({
            title: 'Apply schedule changes?',
            message: `Apply these schedule changes now?\n\n${summary}\n\nRunning tasks are not interrupted; the new value is used from the next wake-up (within 5 minutes).`,
            confirmLabel: 'Apply',
        }))) return;
        setBusy(true); setError(null);
        try {
            const res = await finesService.engineScheduleSave(Object.fromEntries(changed));
            setData(res); setDraft({});
            toast.success(`${res.changed?.length || 0} schedule(s) changed`);
        } catch (e) { setError(errorMessage(e)); }
        setBusy(false);
    };
    const reset = async () => {
        if (!(await confirmDialog({
            title: 'Back to server defaults?',
            message: 'Drop every change made here and go back to the server\'s .env / defaults?',
            confirmLabel: 'Drop changes',
            danger: true,
        }))) return;
        setBusy(true); setError(null);
        try { const res = await finesService.engineScheduleReset(); setData(res); setDraft({}); toast.success(`${res.reset || 0} override(s) dropped`); }
        catch (e) { setError(errorMessage(e)); }
        setBusy(false);
    };

    if (!data && !error) return <LoadingSpinner message="Reading the engine…" />;
    if (!data) return <div role="alert" className="m-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>;

    const repeating = rows.filter((r) => ['minutes', 'seconds'].includes(r.kind));
    const now = dubaiNow();
    const nowHour = now ? Number(now.slice(0, 2)) : -1;

    const repeatingColumns = [
        { key: 'label', header: 'Task', cell: (r) => <><GroupDot group={groupOf(r.key)} />{r.label}</> },
        { key: 'runs', header: 'Runs', className: 'font-mono', sortable: false, cell: (r) => fmt(Number(value(r)), r.kind) },
        { key: 'lastRun', header: 'Last run', className: 'font-mono text-xs', cell: (r) => <LastRun row={r} /> },
        { key: 'nextRun', header: 'Next run', className: 'font-mono text-xs', cell: (r) => r.nextRun || '—' },
    ];
    // a day of the week reads as its name ("Saturday", "never - by hand"), not 5 / 7
    const histValue = (h, v) => (v != null && byKey[h.key]?.kind === 'weekday' ? fmt(v, 'weekday') : String(v ?? '—'));
    const historyColumns = [
        { key: 'at', header: 'When', className: 'font-mono text-xs' },
        { key: 'by', header: 'Who', className: 'text-xs' },
        { key: 'key', header: 'Task', accessor: (h) => byKey[h.key]?.label || h.key },
        { key: 'from', header: 'From', className: 'font-mono', sortable: false, cell: (h) => histValue(h, h.from) },
        { key: 'to', header: 'To', className: 'font-mono', sortable: false, cell: (h) => histValue(h, h.to) },
    ];

    return (
        <div>
            {error && <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

            <Section title="The day at a glance — daily tasks by hour" right={`Asia/Dubai · now ${now || '—'}`}>
                <HourGrid rows={rows} value={value} nowHour={nowHour} />
            </Section>

            <Section title="Repeating tasks">
                <DataTable
                    columns={repeatingColumns}
                    data={repeating}
                    rowKey="key"
                    searchable={false}
                    pagination={false}
                    columnToggle={false}
                    emptyIcon={ICONS.clock}
                    emptyTitle="No repeating tasks"
                />
            </Section>

            {data.warnings?.length > 0 && (
                <div role="note" className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-6 text-sm text-amber-900">
                    <div className="flex items-center gap-2 font-semibold mb-1"><Icon glyph={ICONS.warning} size="sm" tone="warning" />Worth knowing</div>
                    <ul className="list-disc ml-5">{data.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
            )}

            <AutoEjarAccountCard />

            <Section title="All schedules" right="edit a cell, then Save · saved with your name · applied within 5 min · never interrupts a running task">
                <TableContainer>
                    <Table>
                        <TableHead><TableRow>
                            {/* each column wide enough for what it holds - the cells ran into each other (owner, 4 Oct 2026:
                                "الجدول ده كله داخل فى بعض"); the table scrolls sideways instead */}
                            <TableHeader className="min-w-[170px]">Task</TableHeader><TableHeader className="min-w-[300px]">What it does</TableHeader>
                            <TableHeader className="min-w-[250px]">Schedule</TableHeader>
                            <TableHeader className="min-w-[160px]">Last run</TableHeader><TableHeader className="min-w-[160px]">Next run</TableHeader>
                            <TableHeader className="min-w-[120px]">Source</TableHeader>
                        </TableRow></TableHead>
                        <TableBody>
                            {GROUPS.map((g) => {
                                const list = g.keys.map((k) => byKey[k]).filter(Boolean);
                                if (!list.length) return null;
                                return (
                                    <React.Fragment key={g.id}>
                                        <TableRow><TableCell colSpan={6} className="bg-gray-50 font-semibold text-gray-900">
                                            <GroupDot group={g} />{g.title} <span className="font-normal text-xs text-gray-500">· {list.length} task{list.length > 1 ? 's' : ''}</span>
                                        </TableCell></TableRow>
                                        {list.map((r) => {
                                            const [variant, label] = SOURCE[r.source] || SOURCE.default;
                                            const dirty = draft[r.key] !== undefined && String(draft[r.key]) !== String(r.value ?? '');
                                            return (
                                                <TableRow key={r.key} className={dirty ? 'bg-purple-50' : ''}>
                                                    {/* the table's own rule keeps cells on one line (it outranks a class): the text cells wrap */}
                                                    <TableCell className="min-w-[170px]" style={WRAP}>{r.label}</TableCell>
                                                    <TableCell className="text-sm text-gray-600 min-w-[300px] max-w-[380px]" style={WRAP}>{r.help}</TableCell>
                                                    <TableCell className="min-w-[250px]">
                                                        {!r.editable ? (
                                                            <span className="font-mono">{r.kind === 'time' ? `daily at ${r.value}` : fmt(r.value, r.kind)}</span>
                                                        ) : r.kind === 'switch' ? (
                                                            <Select size="sm" className="w-auto" aria-label={`${r.label}: on or off`} value={value(r) === true || value(r) === 'true' ? 'on' : 'off'}
                                                                onChange={(e) => set(r.key, e.target.value === 'on')}>
                                                                <option value="off">off</option>
                                                                <option value="on">on</option>
                                                            </Select>
                                                        ) : r.kind === 'weekday' ? (
                                                            <span className="inline-flex items-center gap-2 whitespace-nowrap">{Number(value(r)) !== NEVER_DAY && <span className="text-xs text-gray-500">every</span>}
                                                                <Select size="sm" className="w-auto" aria-label={`${r.label}: day of the week`} value={String(value(r))}
                                                                    onChange={(e) => set(r.key, Number(e.target.value))}>
                                                                    {WEEKDAYS.map((d, i) => <option key={d} value={String(i)}>{d}</option>)}
                                                                    <option value={String(NEVER_DAY)}>never - by hand</option>
                                                                </Select></span>
                                                        ) : r.kind === 'date' ? (
                                                            <span className="inline-flex items-center gap-2 whitespace-nowrap"><span className="text-xs text-gray-500">from</span>
                                                                <Input type="date" size="sm" className="font-mono w-auto" aria-label={`${r.label}: day`} value={value(r)} onChange={(e) => set(r.key, e.target.value)} /></span>
                                                        ) : r.kind === 'time' ? (
                                                            <span className="inline-flex items-center gap-2 whitespace-nowrap"><span className="text-xs text-gray-500">daily at</span>
                                                                <Input type="time" size="sm" className="font-mono w-auto" aria-label={`${r.label}: daily time`} value={value(r)} onChange={(e) => set(r.key, e.target.value)} /></span>
                                                        ) : (
                                                            <span className="block">
                                                                <span className="inline-flex items-center gap-2 whitespace-nowrap"><span className="text-xs text-gray-500">every</span>
                                                                    <Input type="number" size="sm" min={r.off ? 0 : r.min} max={r.max} className="font-mono w-20" aria-label={`${r.label}: every (${UNIT[r.kind]})`} value={value(r)} onChange={(e) => set(r.key, e.target.value)} />
                                                                    <span className="text-xs text-gray-500">{UNIT[r.kind]}</span></span>
                                                                <span className="block text-[11px] text-gray-500 mt-0.5">allowed {r.min}–{r.max}{r.off ? ' · 0 = off' : ''}</span>
                                                            </span>
                                                        )}
                                                        {dirty && <span className="block text-[11px] text-purple-700 mt-0.5">was {fmt(r.value, r.kind)}</span>}
                                                    </TableCell>
                                                    <TableCell className="font-mono text-xs whitespace-nowrap min-w-[160px]"><LastRun row={r} /></TableCell>
                                                    <TableCell className="font-mono text-xs whitespace-nowrap min-w-[160px]">{r.nextRun || '—'}</TableCell>
                                                    <TableCell>
                                                        <Badge variant={variant} size="sm">{label}</Badge>
                                                        {r.source === 'override' && <span className="block text-[11px] text-gray-500 mt-0.5">start-up {fmt(r.default, r.kind)}</span>}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Section>

            <FormFooter className="mb-6" status={changed.length ? `${changed.length} unsaved change(s)` : 'No unsaved changes'}>
                <Button leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />} disabled={busy} onClick={async () => {
                    // a Refresh reads the server again and drops the edits: asked first when there are some (review 18 Oct 2026)
                    if (changed.length && !(await confirmDialog({ title: `Drop ${changed.length} unsaved change(s)?`, message: 'Refresh reads the schedule from the server again; your unsaved edits are lost.', confirmLabel: 'Refresh', danger: true }))) return;
                    load();
                }}>Refresh</Button>
                {/* its own permission (phase 2, 4 Oct 2026); the server keeps the automatic Ejar switches unless an admin resets */}
                {canReset && <Button leadingIcon={<Icon glyph={ICONS.back} size="sm" />} disabled={busy || !Object.keys(data.overrides || {}).length} onClick={reset}>Back to server defaults</Button>}
                <Button disabled={busy || !changed.length} onClick={() => setDraft({})}>Discard</Button>
                <Button variant="primary" leadingIcon={<Icon glyph={ICONS.save} size="sm" />} isLoading={busy} disabled={busy || !changed.length} onClick={save}>{busy ? 'Saving…' : `Save${changed.length ? ` (${changed.length})` : ''}`}</Button>
            </FormFooter>

            {data.history?.length > 0 && (
                <Section title={`Change history (${data.history.length})`}>
                    <DataTable
                        columns={historyColumns}
                        data={data.history}
                        rowKey={(h, i) => `${h.at}-${h.key}-${i}`}
                        searchable={false}
                        columnToggle={false}
                        emptyTitle="No changes yet"
                    />
                </Section>
            )}
        </div>
    );
}
